import type { Schema } from "amplify/data/resource";

export type BusinessProfile = Schema["BusinessProfile"]["type"];
export type MerchantLifecycle = Schema["MerchantLifecycle"]["type"];
export type UserProfile = Schema["UserProfile"]["type"];
export type Transaction = Schema["Transaction"]["type"];

export interface TrackState {
  stage: string;
  enteredAt: string;
  metadata?: Record<string, unknown>;
}

export interface HistoryEntry {
  track: string;
  from: string;
  to: string;
  triggeredBy: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface ParsedStripeErrors {
  currentlyDue?: string[];
  pendingVerifications?: string[];
  restrictedCapabilities?: string[];
  currentDeadlineStamp?: number;
  requiresProofOfLiveness?: boolean;
  requiresExternalAccount?: boolean;
  rawErrors?: Array<{ code?: string; reason?: string; requirement?: string }>;
  errors?: string[];
  fieldErrors?: Record<string, string[]>;
}

export interface ParsedBusinessErrors {
  stripe?: ParsedStripeErrors;
  replaceableErrors?: string[];
  currentPaymentMethodError?: { message?: string };
  onboardingSetupError?: { error?: string; code?: string; timestamp?: number };
}

export function parseBusinessErrors(
  errors: unknown,
): ParsedBusinessErrors | null {
  if (!errors) return null;
  try {
    if (typeof errors === "string") return JSON.parse(errors);
    if (typeof errors === "object") return errors as ParsedBusinessErrors;
  } catch {
    // invalid JSON
  }
  return null;
}

export function parseTracks(tracks: unknown): Record<string, TrackState> {
  try {
    if (typeof tracks === "string") return JSON.parse(tracks);
    if (tracks && typeof tracks === "object")
      return tracks as Record<string, TrackState>;
  } catch {
    // malformed JSON
  }
  return {};
}

export function parseHistory(history: unknown): HistoryEntry[] {
  try {
    if (typeof history === "string") return JSON.parse(history);
    if (Array.isArray(history)) return history as HistoryEntry[];
  } catch {
    // malformed JSON
  }
  return [];
}

export function formatEpoch(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatIso(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatRelativeTime(date: Date | string | number): string {
  const now = Date.now();
  const then =
    typeof date === "number" ? date * 1000 : new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatEpoch(then / 1000);
}
